import type { MiniBannerTargetType } from '@masters-club/types';

export interface MiniBannerImage {
  id: number;
  miniBannerId: number;
  imageId: number;
  imageUrl?: string | null;
  /** '19:2' — десктоп, '21:9' — мобильный */
  aspectRatio: string;
  languageCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MiniBannerTarget {
  id: number;
  miniBannerId: number;
  targetType: MiniBannerTargetType;
  targetId: number;
  /**
   * Разрешённый родительский курс (null если цель указывает на несуществующий урок/модуль).
   *  - COURSE:      resolvedCourseId = targetId
   *  - MODULE:      resolvedCourseId = родительский курс модуля
   *  - MODULE_ITEM: resolvedCourseId = родительский курс урока
   *  - LIVESTREAM:  null
   */
  resolvedCourseId: number | null;
  /**
   * Разрешённый родительский модуль:
   *  - COURSE:      null
   *  - MODULE:      resolvedModuleId = targetId
   *  - MODULE_ITEM: resolvedModuleId = родительский модуль урока
   *  - LIVESTREAM:  null
   */
  resolvedModuleId: number | null;
  /** Человекочитаемое название самой цели (название курса/модуля/урока). */
  targetName: string | null;
  /** Название родительского курса (для MODULE/MODULE_ITEM). */
  courseName: string | null;
  /** Название родительского модуля (для MODULE_ITEM). */
  moduleName: string | null;
  createdAt: string;
}

export interface MiniBannerTranslation {
  languageCode: string;
  data: {
    title?: string;
    subtitle?: string;
    buttonText?: string;
  };
}

export interface MiniBanner {
  id: number;
  linkUrl: string | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  viewsCount: number;
  clicksCount: number;
  uniqueViewsCount: number;
  uniqueClicksCount: number;
  createdAt: string;
  updatedAt: string;
  images: MiniBannerImage[];
  targets: MiniBannerTarget[];
  translations: MiniBannerTranslation[];
}

export interface MiniBannerImageInput {
  imageId: number;
  imageUrl?: string | null;
  /** '19:2' — десктоп, '21:9' — мобильный */
  aspectRatio: string;
  languageCode: string | null;
}

export interface MiniBannerTargetInput {
  targetType: MiniBannerTargetType;
  targetId: number;
  /**
   * Разрешённый курс — используется админкой для восстановления
   * каскадного выбора Курс→Модуль→Урок при редактировании.
   * В backend не отправляется (сервер вычислит сам).
   */
  resolvedCourseId?: number | null;
  /** Аналогично для модуля */
  resolvedModuleId?: number | null;
}

export interface MiniBannerTranslationInput {
  languageCode: string;
  title: string | null;
  subtitle: string | null;
  buttonText: string | null;
}

export interface MiniBannerFormData {
  images: MiniBannerImageInput[];
  targets: MiniBannerTargetInput[];
  translations: MiniBannerTranslationInput[];
  linkUrl: string | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

export const TARGET_TYPE_LABELS = {
  COURSE: 'Курс',
  MODULE: 'Модуль',
  MODULE_ITEM: 'Урок',
  LIVESTREAM: 'Трансляция',
} as const;

/**
 * Мини-баннер показывается внутри урока тонкой широкой карточкой между
 * контентом и кнопкой "Следующий". Два адаптивных слота:
 *  - 19:2 (десктоп, обязательный) — тонкая широкая полоска;
 *  - 21:9 (мобильный, опциональный) — на мобилке fallback на 19:2.
 */
export const MINI_BANNER_DESKTOP_ASPECT_RATIO = '19:2';
export const MINI_BANNER_MOBILE_ASPECT_RATIO = '21:9';
/** Соотношение для десктопного слота — дефолт при загрузке/совместимости. */
export const MINI_BANNER_DEFAULT_ASPECT_RATIO = MINI_BANNER_DESKTOP_ASPECT_RATIO;

export interface MiniBannerDeviceSlot {
  aspectRatio: string;
  label: string;
  description: string;
  required: boolean;
}

export const MINI_BANNER_DEVICE_SLOTS: readonly MiniBannerDeviceSlot[] = [
  {
    aspectRatio: MINI_BANNER_DESKTOP_ASPECT_RATIO,
    label: 'Десктоп',
    description: '19:2, рекомендуемый размер 1900×200',
    required: true,
  },
  {
    aspectRatio: MINI_BANNER_MOBILE_ASPECT_RATIO,
    label: 'Мобильный',
    description: '21:9, рекомендуемый размер 1080×463',
    required: false,
  },
] as const;

/**
 * URL картинки для превью в списке/дашборде.
 * Приоритет: десктоп 19:2 (универсальный → любой) → любая доступная.
 */
export function getMiniBannerThumbnailUrl(banner: MiniBanner): string | null {
  const desktop = banner.images.filter(
    (img) => img.aspectRatio === MINI_BANNER_DESKTOP_ASPECT_RATIO,
  );
  const desktopUniversal = desktop.find((img) => img.languageCode === null);
  const universal = banner.images.find((img) => img.languageCode === null);
  const first = desktopUniversal ?? desktop[0] ?? universal ?? banner.images[0];
  return first?.imageUrl ?? null;
}

/** Вытащить заголовок из первого доступного перевода */
export function getMiniBannerTitle(banner: MiniBanner): string | null {
  for (const t of banner.translations) {
    if (t.data.title) { return t.data.title; }
  }
  return null;
}

/**
 * CTR = клики / просмотры * 100 (в %)
 */
export function calculateMiniBannerCTR(banner: MiniBanner): number {
  if (banner.viewsCount === 0) { return 0; }
  return (banner.clicksCount / banner.viewsCount) * 100;
}

/**
 * Человекочитаемое описание цели для таблицы.
 * Например:
 *   COURSE "Введение" → "Курс: Введение"
 *   MODULE "Старт" (в курсе "Введение") → "Модуль: Старт (Введение)"
 *   MODULE_ITEM "Урок 1" → "Урок: Урок 1 (Модуль · Курс)"
 */
export function formatMiniBannerTargetLabel(target: MiniBannerTarget): string {
  const typeLabel = TARGET_TYPE_LABELS[target.targetType];
  const name = target.targetName ?? `#${target.targetId}`;

  if (target.targetType === 'COURSE') {
    return `${typeLabel}: ${name}`;
  }

  if (target.targetType === 'MODULE') {
    const course = target.courseName ? ` · ${target.courseName}` : '';
    return `${typeLabel}: ${name}${course}`;
  }

  if (target.targetType === 'LIVESTREAM') {
    return `${typeLabel}: ${name}`;
  }

  // MODULE_ITEM
  const parents = [target.moduleName, target.courseName].filter(Boolean).join(' · ');
  return parents ? `${typeLabel}: ${name} (${parents})` : `${typeLabel}: ${name}`;
}
